import {api} from './api'

export interface ICustomer {
    id: string
    name: string
    email: string
    phone: string
    shopify_customer_id: string
    address_1: string
    address_2: string
    city: string
    state: string
    zip_code: string
    country: string
    created_at: string
    updated_at: string
}

export interface ILocation {
    id: string
    name: string
}

export interface IOrder {
    id: string | number
    order_number: string
    customer: ICustomer
    locations: ILocation
    order_date: string
    currency: string
    total_price: number
    total_discount: number
    source_order_id: string
    confirmed_delivery_date: string
    created_at: string
    updated_at: string
    order_line_items: IOrderLineItems[]
    shippingAddress: any;
    deliveryDate: any;
    OrderLineItems: any;
}

export interface IOrderLineItems {
    booking_slot?: ILineItemBookingSlot
    created_at: string
    id: string
    location_id: string
    name: string
    order_id: string
    price: number
    quantity: number
    updated_at: string
}

export interface ILineItemBookingSlot {
    class_id?: string
    created_at: string
    customer_id: string
    date_end: string
    date_start: string
    id: string
    location_id: string
    order_line_item_id: string
    status: string
    updated_at: string
}

interface IFilters {
    page?: number,
    limit?: number
}

interface IListResponse<T> {
    orders: T[]
    records_count: number
    next_page: number | null
    previous_page: number | null
}

export const ordersApi = api.injectEndpoints({
    endpoints: (build) => ({
        getTopOrders: build.query<IListResponse<IOrder>, any>({
            query: (params) => {
                const {startDate, endDate, ...otherParams} = params;
                let url = '/orders';
                const queryParams = new URLSearchParams(otherParams as any);

                if (startDate && endDate) {
                    queryParams.append('filter_by', `delivery_date:">=${startDate}" delivery_date:"<=${endDate}"`);
                }

                return {
                    url: `${url}?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            providesTags: (result) => {
                return result
                    ? [
                        ...result.orders.map(({id}) => ({
                            type: 'Orders' as const,
                            id
                        })),
                        {type: 'Orders', id: 'PARTIAL-LIST'}
                    ]
                    : [{type: 'Orders', id: 'PARTIAL-LIST'}]
            }
        }),
        bulkReleaseOrders: build.mutation<any, { orderIds: string }>({
            query: ({orderIds}) => ({
                url: `/orders/bulk/release?orderIds=${orderIds}`,
                method: 'GET',
            }),
        }),
        getOrder: build.query<{ order: any }, string>({
            query: (orderId) => {
                return {url: `/orders/${orderId}`, method: 'GET'}
            },
            keepUnusedDataFor: 0, // This disables caching
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg
            }
        }),
        updateOrderNotificationStatus: build.mutation<any, { orderId: string; body: Partial<any> }>({
            query: ({orderId, body}) => ({
                url: `/orders/${orderId}/notification`,
                method: 'PUT',
                body,
            }),
        }),
        cancelBooking: build.mutation<void, string>({
            query: (bookingSlotId) => ({
                url: `/booking_slots/${bookingSlotId}/cancel`,
                method: 'GET',
            }),
            invalidatesTags: ['Orders'],
        }),
        updateBookingSlotNote: build.mutation<void, { id: string; note: string }>({
            query: ({id, note}) => ({
                url: `/booking_slots/${id}`,
                method: 'PUT',
                body: {note},
            }),
            invalidatesTags: (result, error, {id}) => [{type: 'BookingSlots', id}],
        }),
        createDraftOrder: build.mutation<any, any>({
            query: (draftOrderInput) => ({
                url: '/shopify/draft-orders',
                method: 'POST',
                body: draftOrderInput,
            }),
            invalidatesTags: ['Orders'],
        }),
        releaseOrder: build.mutation<any, { orderId: string; body: any }>(
            {
                query: ({orderId, body}) => ({
                    url: `/orders/${orderId}/release`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (result, error, {orderId}) => [
                    {type: 'Orders', id: orderId},
                ],
            }
        ),
        updateOrder: build.mutation<any, { orderId: string; body: any }>(
            {
                query: ({orderId, body}) => ({
                    url: `/orders/${orderId}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (result, error, {orderId}) => [
                    {type: 'Orders', id: orderId},
                ],
            }
        ),
    })
})

export const {
    useGetTopOrdersQuery,
    useGetOrderQuery,
    useUpdateOrderNotificationStatusMutation,
    useCancelBookingMutation,
    useUpdateBookingSlotNoteMutation,
    useCreateDraftOrderMutation,
    useReleaseOrderMutation,
    useUpdateOrderMutation,
    useBulkReleaseOrdersMutation
} = ordersApi
